/**
 * Locations Catalog Service
 */

import { apiClient } from '@/api/core/apiClient';
import { API_CONFIG } from '@/api/core/apiConfig';
import type { ApiResponse } from '@/api/shared/types';
import { buildQueryString } from '@/api/shared';
import type { CatalogScope } from '@/api/domains/catalog/shared/catalogScope';
import type { LC_GetLocationsOut, Location } from './locations.types';
import { mapLocationOutToModel } from './locations.mapper';


export class LocationsCatalogService {
  /**
   * Get available locations catalog
   * GET /v{version}/catalog/locations
   * By default returns only active locations for the user's company; pass includeInactive
   * to get all (status=all) and scope: 'holding' to widen to every company in the holding.
   * Omitting both keeps the original URL for existing consumers.
   */
  async getLocations(
    signal?: AbortSignal,
    options?: { includeInactive?: boolean; scope?: CatalogScope },
  ): Promise<ApiResponse<Location[]>> {
    const queryString = buildQueryString({
      ...(options?.includeInactive ? { status: 'all' } : {}),
      ...(options?.scope === 'holding' ? { scope: 'holding' } : {}),
    });
    const endpoint = `${API_CONFIG.ENDPOINTS.CATALOG.LOCATIONS}${queryString}`;
    const response = await apiClient.get<LC_GetLocationsOut>(endpoint, { signal });

    if (response.success && response.data) {
      return {
        ...response,
        data: response.data.map(mapLocationOutToModel),
      };
    }

    return { ...response, data: null };
  }
}

export const locationsCatalogService = new LocationsCatalogService();
